import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Color, HR_EMPLOYEES_PATH, HR_EMPLOYEES_ROUTE, HR_FORMER_EMPLOYEES_PATH, Size, employeeRoute, pageKindLineText, t,
  type ColumnDef, type EmployeeListAnswer, type PageSize, type SortState, viewerScopeKey,
} from '@bms2/shared';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { ListViewer } from '../../components/composites/ListViewer';
import { Button } from '../../components/controls';
import { PageModule } from '../../shell/PageModule';
import { useApi, writeGate } from '../records/api';
import { HireWindow } from './HireWindow';
import { HrPagesNav } from './HrPagesNav';
import styles from './HrPages.module.css';

export { HR_EMPLOYEES_PATH, HR_EMPLOYEES_ROUTE };

const COLUMNS: readonly ColumnDef[] = [
  { key: 'name', label: t('hr.employees.col.name'), role: 'primary', type: 'text' },
  { key: 'title', label: t('hr.employees.col.title'), role: 'secondary', type: 'text' },
  { key: 'department', label: t('hr.employees.col.department'), role: 'secondary', type: 'text' },
  { key: 'startDate', label: t('hr.employees.col.startDate'), role: 'date', type: 'date' },
];

/** Former Employees reads the last day beside the first (decision 3181). */
const FORMER_COLUMNS: readonly ColumnDef[] = [...COLUMNS, { key: 'endDate', label: t('hr.employees.col.endDate'), role: 'date', type: 'date' }];

type EmployeesView = 'current' | 'former';

/**
 * The Employee list (piece 34, Module shell and visibility; decision 1467): a
 * List Viewer over every active employment at the Organization's own Company,
 * each row opening that job's Employee page (decision 3180). 🔨 It lists employments rather than
 * logins, so a worker with no login is on it, and the route behind it refuses
 * a viewer without `hr.employees.view` in a sentence the panel draws.
 *
 * 🔒 Decision 3181: its second view, Former Employees, lists the jobs that have
 * ended; + Hire opens the Hire window, which `/hr/hire` opens on arrival.
 */
export function EmployeesPage({ apiBaseUrl = '', openHire = false }: { apiBaseUrl?: string; openHire?: boolean }) {
  const navigate = useNavigate();
  const [view, setView] = useState<EmployeesView>('current');
  const fetched = useApi<EmployeeListAnswer>(view === 'former' ? HR_FORMER_EMPLOYEES_PATH : HR_EMPLOYEES_PATH, apiBaseUrl);
  const [sort, setSort] = useState<SortState | null>({ key: 'name', direction: 'asc' });
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const [hiring, setHiring] = useState(openHire);
  // 🔒 + Hire draws for a holder of `hr.hire` alone, off the list's own answer (T2284): the window used to draw for every reader and refuse on press.
  const mayHire = writeGate(fetched.state, 'mayHire');
  const empty = view === 'former' ? t('hr.employees.formerEmpty') : t('hr.employees.empty');
  return (
    <PageModule
      title={t('hr.employees.title')}
      subtitle={pageKindLineText({ page: 'named', route: HR_EMPLOYEES_ROUTE })}
      icon="hr"
      newAction={mayHire ? <span data-hr-hire="open"><Button size={Size.Small} color={Color.Navy} text={[t('hr.hire.button')]} actions={[() => setHiring(true)]} /></span> : undefined}
    >
      <div className={styles.page} data-page="hr-employees" data-hr-view={view}>
      <HrPagesNav current={HR_EMPLOYEES_ROUTE} />
      <AsyncStatePanel state={fetched.state} emptyAction={empty} onRetry={fetched.reload}>
        {(answer) => (
          <div data-hr-employees={String(answer.rows.length)}>
            <ListViewer
              viewStore={{ scopeKey: viewerScopeKey('hr-employees'), apiBaseUrl }}
              recordType={t('hr.employees.title')}
              views={[{ id: 'current', name: t('hr.employees.view.current') }, { id: 'former', name: t('hr.employees.view.former') }]}
              currentViewId={view}
              onViewChange={(id) => setView(id === 'former' ? 'former' : 'current')}
              columns={view === 'former' ? FORMER_COLUMNS : COLUMNS}
              rows={answer.rows}
              idOf={(row) => row.employmentId}
              read={(row, key) => (row as unknown as Record<string, unknown>)[key] ?? '—'}
              sort={sort}
              onSortChange={setSort}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              emptyAction={empty}
              onNavigate={(row) => navigate(employeeRoute(row.employmentId))}
            />
          </div>
        )}
      </AsyncStatePanel>
      </div>
      {/* 🔨 Mounted only while open, so each opening starts empty. */}
      {hiring ? <HireWindow apiBaseUrl={apiBaseUrl} onClose={() => setHiring(false)} onHired={(answer) => navigate(employeeRoute(answer.employmentId))} /> : null}
    </PageModule>
  );
}
